import { ReactNode, useState, useEffect } from "react";
import { SidebarProvider, SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { ArrowLeft, LogIn, LogOut, Home, HelpCircle } from "lucide-react";
import { PhoneLoginDialog } from "@/components/auth/PhoneLoginDialog";
import { LanguageSelector } from "@/components/LanguageSelector";
import { supabase } from "@/integrations/supabase/client";
import { OnboardingTutorial } from "@/components/OnboardingTutorial";

interface AppLayoutProps {
  children: ReactNode;
}


export function AppLayout({ children }: AppLayoutProps) {
  const { isAuthenticated, initialized, signOut, roles, getFirstName, getLastName } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [showPhoneLogin, setShowPhoneLogin] = useState(false);
  const [userStatus, setUserStatus] = useState<string | null>(null);
  const [showTutorial, setShowTutorial] = useState(false);

  // Le status vient des roles
  useEffect(() => {
    if (roles && roles.length > 0) {
      setUserStatus(roles[0].status);
    }
  }, [roles]);

  return (
    <SidebarProvider defaultOpen={true}>
      <div className="min-h-screen flex flex-col w-full">
        {/* Header global au-dessus de tout */}
        <header className="fixed top-0 left-0 right-0 z-[100] h-14 flex items-center justify-between px-2 sm:px-4 border-b border-border bg-background backdrop-blur-md shadow-sm shrink-0">
          <div className="flex items-center space-x-1 sm:space-x-2 min-w-0">
            <SidebarTrigger 
              data-onboarding="sidebar-trigger"
              className="ml-1 h-6 w-6 shrink-0 hover:bg-accent rounded-md transition-colors" 
            />
            
            {/* Bouton Accueil */}
            <Button
              data-onboarding="home-button"
              onClick={() => {
                let targetPath = '/';
                if (initialized && isAuthenticated()) {
                  const activeRole = roles.find(r => r.status === 'actif' || r.status === 'active');
                  const role = activeRole?.role;
                  if (role === 'admin') {
                    targetPath = '/dashboard-admin';
                  } else if (role === 'client') {
                    targetPath = '/dashboard-client';
                  } else if (role === 'prestataire') {
                    targetPath = '/dashboard-prestataire';
                  }
                }
                navigate(targetPath);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground px-2 flex items-center gap-1"
              title="Retour à la page principale"
            >
              <Home className="h-4 w-4" />
              {!isMobile && <span className="text-sm">Accueil</span>}
            </Button>

            {/* Bouton Retour - à droite du bouton Accueil */}
            <Button
              data-onboarding="back-button"
              onClick={() => {
                if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  navigate('/');
                }
              }}
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground px-2 flex items-center gap-1"
              title="Retour à la page précédente"
            >
              <ArrowLeft className="h-4 w-4" />
              {!isMobile && <span className="text-sm">Retour</span>}
            </Button>
          
            {/* Affichage du rôle et statut */}
            {initialized && isAuthenticated() && roles && roles.length > 0 && (() => {
              const activeRole = roles.find(r => {
                const status = (r.status || '').toLowerCase().trim();
                return status === 'actif' || status === 'active';
              }) || roles[0];
              
              const normalizedStatus = (activeRole.status || '').toLowerCase().trim();
              const shouldShowStatusBadge = activeRole.status && 
                normalizedStatus !== 'actif' && 
                normalizedStatus !== 'active';
              
              return (
                <div data-onboarding="role-badge" className="flex items-center gap-2 ml-2">
                  <span className="text-sm font-medium text-foreground capitalize hidden sm:inline">
                    {activeRole.role === 'admin' ? 'Administrateur' :
                     activeRole.role === 'prestataire' ? 'Prestataire' :
                     activeRole.role === 'client' ? 'Client' : activeRole.role}
                  </span>
                  {shouldShowStatusBadge && (
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                      normalizedStatus === 'prospect' ? 'bg-blue-500/20 text-blue-700 dark:text-blue-400' :
                      normalizedStatus === 'draft' ? 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400' :
                      'bg-gray-500/20 text-gray-700 dark:text-gray-400'
                    }`}>
                      {activeRole.status}
                    </span>
                  )}
                </div>
              );
            })()}
            
            {/* Placeholder pour le badge rôle quand non connecté */}
            {(!initialized || !isAuthenticated() || !roles || roles.length === 0) && (
              <div data-onboarding="role-badge" className="hidden" />
            )}
          </div>

        {/* Boutons d'action à droite */}
        <div className="flex items-center space-x-1 sm:space-x-2 shrink-0">

          {/* Bouton connexion/déconnexion */}
          {initialized && (
            <div data-onboarding="auth-button">
              {!isAuthenticated() ? (
                <Button
                  onClick={() => navigate('/auth?origin=nav')}
                  variant="default"
                  size="sm"
                  className="px-2"
                >
                  <LogIn className="h-4 w-4" />
                  <span className="hidden sm:ml-2 sm:inline">Connexion</span>
                </Button>
              ) : (
                <Button
                  onClick={async () => {
                    await signOut();
                    navigate('/');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-foreground px-2"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="hidden sm:ml-2 sm:inline">Déconnexion</span>
                </Button>
              )}
            </div>
          )}
          
          {/* Language Selector */}
          <div data-onboarding="language-selector">
            <LanguageSelector />
          </div>
          
          {/* Bouton aide / tutoriel */}
          <Button
            data-onboarding="help-button"
            onClick={() => setShowTutorial(true)}
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-foreground"
            title="Afficher l'aide"
          >
            <HelpCircle className="h-5 w-5" />
          </Button>
        </div>
      </header>
      
      {/* Tutoriel d'onboarding */}
      <OnboardingTutorial 
        forceShow={showTutorial} 
        onClose={() => setShowTutorial(false)} 
      />

        <div className="flex w-full pt-14 h-screen overflow-hidden">
          <AppSidebar />
          
          <div className="flex-1 flex flex-col min-w-0 min-h-0">
            {/* Contenu principal */}
            <main className="flex-1 overflow-auto min-w-0 min-h-0">
              <div className="w-full min-w-0">
                {children}
              </div>
            </main>
          </div>
        </div>
        
        <PhoneLoginDialog open={showPhoneLogin} onOpenChange={setShowPhoneLogin} />
      </div>
    </SidebarProvider>
  );
}